import { useMemo } from "react";
import {
  KeyRound,
  Ticket,
  Users,
  CalendarRange,
  Bell,
  Sparkles,
  ClipboardCheck,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import { EVENTO_CONFIG, EVENTO_WIZARD } from "@/constants/testIds";
import {
  TIPO_ACCESO,
  TIPO_ACCESO_LABEL,
  formatearFechaCorta,
} from "@/data/adminEventosStore";

/**
 * Paso 2 del Wizard · Configuración del evento.
 * Controlado desde `EventoWizard` mediante `valores` y `onChange(campo, valor)`.
 * No implementa aún: envío de notificaciones, lógica de reasignación,
 * cambios de nombre en tiempo real, ni lista de espera. Sólo persiste
 * la configuración.
 */
export default function PasoConfiguracion({ valores, onChange, errores }) {
  const setNum = (campo) => (e) => {
    const raw = e.target.value;
    onChange(campo, raw === "" ? "" : Number(raw));
  };

  return (
    <div
      data-testid={EVENTO_WIZARD.stepContent(2)}
      className="flex flex-col gap-6"
    >
      <div>
        <span className="overline text-accent">Paso 2</span>
        <h2 className="mt-1 font-heading text-2xl sm:text-3xl leading-tight">
          Configuración
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">
          Reglas operativas del evento. Estos parámetros aplican antes de
          construir el plano y la venta.
        </p>
      </div>

      {/* Sección 1 · Acceso */}
      <Seccion
        icon={KeyRound}
        titulo="Acceso"
        descripcion="Quién puede asistir y bajo qué condiciones."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="flex flex-col gap-2 md:col-span-3">
            <Label>Tipo de acceso *</Label>
            <RadioGroup
              data-testid={EVENTO_CONFIG.tipoAcceso}
              value={valores.tipoAcceso || ""}
              onValueChange={(v) => onChange("tipoAcceso", v)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {[TIPO_ACCESO.SOLO_SOCIOS, TIPO_ACCESO.SOCIOS_INVITADOS].map(
                (k) => (
                  <label
                    key={k}
                    className="flex items-center gap-3 rounded-xl border hairline bg-background px-4 py-3 cursor-pointer hover:border-border transition-colors"
                  >
                    <RadioGroupItem
                      value={k}
                      id={`cfg-acc-${k}`}
                      data-testid={EVENTO_CONFIG.tipoAccesoOpcion(k)}
                    />
                    <span className="text-sm">{TIPO_ACCESO_LABEL[k]}</span>
                  </label>
                ),
              )}
            </RadioGroup>
            {errores.tipoAcceso && (
              <p className="text-xs text-destructive">{errores.tipoAcceso}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="cfg-edad">Edad mínima *</Label>
            <div className="relative">
              <Input
                id="cfg-edad"
                data-testid={EVENTO_CONFIG.edadMinima}
                type="number"
                min={0}
                max={99}
                value={valores.edadMinima ?? ""}
                onChange={setNum("edadMinima")}
                className="bg-background pr-14"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                años
              </span>
            </div>
            {errores.edadMinima && (
              <p className="text-xs text-destructive">{errores.edadMinima}</p>
            )}
          </div>

          <div className="flex flex-col gap-2 md:col-span-2">
            <Label htmlFor="cfg-limite">
              Límite máximo de boletos por usuario *
            </Label>
            <div className="relative">
              <Input
                id="cfg-limite"
                data-testid={EVENTO_CONFIG.limiteBoletos}
                type="number"
                min={1}
                max={200}
                value={valores.limiteBoletosPorUsuario ?? ""}
                onChange={setNum("limiteBoletosPorUsuario")}
                className="bg-background pr-20"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                boletos
              </span>
            </div>
            {errores.limiteBoletosPorUsuario && (
              <p className="text-xs text-destructive">
                {errores.limiteBoletosPorUsuario}
              </p>
            )}
          </div>
        </div>
      </Seccion>

      {/* Sección 2 · Venta */}
      <Seccion
        icon={Ticket}
        titulo="Venta"
        descripcion="Ventana de tiempo en que el evento estará disponible para la compra de boletos."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-xl border hairline p-4 flex flex-col gap-3">
            <span className="overline text-muted-foreground">
              Inicio de venta
            </span>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="cfg-venta-inicio-fecha">Fecha *</Label>
                <Input
                  id="cfg-venta-inicio-fecha"
                  data-testid={EVENTO_CONFIG.ventaInicioFecha}
                  type="date"
                  value={valores.ventaInicioFecha || ""}
                  onChange={(e) =>
                    onChange("ventaInicioFecha", e.target.value)
                  }
                  className="bg-background"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="cfg-venta-inicio-hora">Hora</Label>
                <Input
                  id="cfg-venta-inicio-hora"
                  data-testid={EVENTO_CONFIG.ventaInicioHora}
                  type="time"
                  value={valores.ventaInicioHora || ""}
                  onChange={(e) =>
                    onChange("ventaInicioHora", e.target.value)
                  }
                  className="bg-background"
                />
              </div>
            </div>
            {errores.ventaInicioFecha && (
              <p className="text-xs text-destructive">
                {errores.ventaInicioFecha}
              </p>
            )}
          </div>

          <div className="rounded-xl border hairline p-4 flex flex-col gap-3">
            <span className="overline text-muted-foreground">
              Cierre de venta
            </span>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label htmlFor="cfg-venta-cierre-fecha">Fecha *</Label>
                <Input
                  id="cfg-venta-cierre-fecha"
                  data-testid={EVENTO_CONFIG.ventaCierreFecha}
                  type="date"
                  value={valores.ventaCierreFecha || ""}
                  onChange={(e) =>
                    onChange("ventaCierreFecha", e.target.value)
                  }
                  className="bg-background"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="cfg-venta-cierre-hora">Hora</Label>
                <Input
                  id="cfg-venta-cierre-hora"
                  data-testid={EVENTO_CONFIG.ventaCierreHora}
                  type="time"
                  value={valores.ventaCierreHora || ""}
                  onChange={(e) =>
                    onChange("ventaCierreHora", e.target.value)
                  }
                  className="bg-background"
                />
              </div>
            </div>
            {errores.ventaCierreFecha && (
              <p className="text-xs text-destructive">
                {errores.ventaCierreFecha}
              </p>
            )}
          </div>
        </div>

        <label className="mt-3 flex items-center gap-3 rounded-xl border border-dashed border-border/70 bg-muted/30 px-4 py-3 text-sm text-muted-foreground cursor-pointer">
          <Checkbox
            data-testid={EVENTO_CONFIG.permitirPausarVenta}
            checked={!!valores.permitirPausarVenta}
            onCheckedChange={(v) => onChange("permitirPausarVenta", !!v)}
          />
          <span>
            Permitir pausar la venta posteriormente
            <span className="ml-2 text-[11px] uppercase tracking-widest text-accent">
              Arquitectura
            </span>
          </span>
        </label>
      </Seccion>

      {/* Sección 3 · Capacidad */}
      <Seccion
        icon={Users}
        titulo="Capacidad"
        descripcion="Se calculará automáticamente cuando exista el plano del salón."
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-muted grid place-items-center text-muted-foreground ring-1 ring-border/70">
            <Users className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <div>
            <p
              data-testid={EVENTO_CONFIG.capacidadPlaceholder}
              className="font-heading text-2xl leading-none"
            >
              — personas
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              La capacidad total se derivará del plano y la asignación de
              mesas. No editable manualmente.
            </p>
          </div>
        </div>
      </Seccion>

      {/* Sección 4 · Configuraciones especiales */}
      <Seccion
        icon={Sparkles}
        titulo="Configuraciones especiales"
        descripcion="Se almacena únicamente la configuración; la lógica se implementará en sprints posteriores."
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <CheckboxRow
            testId={EVENTO_CONFIG.permitirListaEspera}
            checked={!!valores.permitirListaEspera}
            onChange={(v) => onChange("permitirListaEspera", v)}
            titulo="Permitir lista de espera"
            hint="No implementar todavía"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.permitirReasignacionMesas}
            checked={!!valores.permitirReasignacionMesas}
            onChange={(v) => onChange("permitirReasignacionMesas", v)}
            titulo="Permitir reasignación administrativa de mesas"
            hint="Activado por defecto"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.permitirCambioNombres}
            checked={!!valores.permitirCambioNombres}
            onChange={(v) => onChange("permitirCambioNombres", v)}
            titulo="Permitir cambios de nombre de asistentes"
            hint="Activado por defecto"
          />
        </div>
      </Seccion>

      {/* Sección 5 · Notificaciones */}
      <Seccion
        icon={Bell}
        titulo="Notificaciones"
        descripcion="Selecciona los eventos que deberán generar avisos al socio. El envío se habilitará en un sprint posterior."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CheckboxRow
            testId={EVENTO_CONFIG.notifInicioVenta}
            checked={!!valores.notifInicioVenta}
            onChange={(v) => onChange("notifInicioVenta", v)}
            titulo="Notificar cuando inicie la venta"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.notifPausaVenta}
            checked={!!valores.notifPausaVenta}
            onChange={(v) => onChange("notifPausaVenta", v)}
            titulo="Notificar cuando se pause la venta"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.notifCambioSalon}
            checked={!!valores.notifCambioSalon}
            onChange={(v) => onChange("notifCambioSalon", v)}
            titulo="Notificar cuando cambie de salón"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.notifCambioMesa}
            checked={!!valores.notifCambioMesa}
            onChange={(v) => onChange("notifCambioMesa", v)}
            titulo="Notificar cuando cambie de mesa"
          />
          <CheckboxRow
            testId={EVENTO_CONFIG.notifCancelacion}
            checked={!!valores.notifCancelacion}
            onChange={(v) => onChange("notifCancelacion", v)}
            titulo="Notificar cuando se cancele"
          />
        </div>
      </Seccion>

      {/* Sección 6 · Resumen */}
      <Resumen valores={valores} />
    </div>
  );
}

/* ---------- Subcomponentes ---------- */

function Seccion({ icon: Icon, titulo, descripcion, children }) {
  return (
    <section className="rounded-2xl border hairline bg-card p-6 md:p-8 flex flex-col gap-5">
      <header className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-accent/10 ring-1 ring-accent/30 text-accent grid place-items-center shrink-0">
          <Icon className="w-4.5 h-4.5" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading text-lg leading-tight">{titulo}</h3>
          {descripcion && (
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              {descripcion}
            </p>
          )}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

function CheckboxRow({ testId, checked, onChange, titulo, hint }) {
  return (
    <label className="flex items-start gap-3 rounded-xl border hairline bg-background px-4 py-3 cursor-pointer hover:border-border transition-colors">
      <Checkbox
        data-testid={testId}
        checked={checked}
        onCheckedChange={(v) => onChange(!!v)}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <p className="text-sm leading-tight">{titulo}</p>
        {hint && (
          <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>
        )}
      </div>
    </label>
  );
}

function Resumen({ valores }) {
  const items = useMemo(() => {
    const arr = [];
    arr.push(
      valores.tipoAcceso === TIPO_ACCESO.SOCIOS_INVITADOS
        ? "Socios e invitados"
        : "Solo socios",
    );
    if (valores.edadMinima || valores.edadMinima === 0)
      arr.push(`Edad mínima: ${valores.edadMinima} años`);
    if (valores.limiteBoletosPorUsuario)
      arr.push(`Máximo: ${valores.limiteBoletosPorUsuario} boletos por usuario`);
    return arr;
  }, [valores]);

  const rangoVenta = useMemo(() => {
    const inicioF = valores.ventaInicioFecha || "";
    const cierreF = valores.ventaCierreFecha || "";
    if (!inicioF && !cierreF) return null;
    const inicioTxt = inicioF
      ? `${formatearFechaCorta(inicioF)}${valores.ventaInicioHora ? ` · ${valores.ventaInicioHora}` : ""}`
      : "—";
    const cierreTxt = cierreF
      ? `${formatearFechaCorta(cierreF)}${valores.ventaCierreHora ? ` · ${valores.ventaCierreHora}` : ""}`
      : "—";
    return { inicioTxt, cierreTxt };
  }, [valores]);

  const notifsActivas = [
    valores.notifInicioVenta && "Inicio",
    valores.notifPausaVenta && "Pausa",
    valores.notifCambioSalon && "Cambio de salón",
    valores.notifCambioMesa && "Cambio de mesa",
    valores.notifCancelacion && "Cancelación",
  ].filter(Boolean);

  return (
    <section
      data-testid={EVENTO_CONFIG.resumen}
      className="rounded-2xl border hairline bg-muted/30 p-6 md:p-8 flex flex-col gap-4"
    >
      <header className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent ring-1 ring-primary/20 dark:ring-accent/30 grid place-items-center">
          <ClipboardCheck className="w-4 h-4" strokeWidth={1.7} />
        </div>
        <div>
          <h3 className="font-heading text-lg leading-tight">Resumen</h3>
          <p className="text-xs text-muted-foreground">
            Vista previa de la configuración capturada.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {items.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded-full bg-background border hairline px-3 py-1 text-xs"
          >
            {t}
          </span>
        ))}
      </div>

      <div className="rounded-xl bg-background border hairline p-4 flex items-center gap-3 flex-wrap">
        <CalendarRange className="w-4 h-4 text-accent" strokeWidth={1.7} />
        {rangoVenta ? (
          <p className="text-sm">
            <span className="text-muted-foreground">Venta:</span>{" "}
            <span className="font-medium">{rangoVenta.inicioTxt}</span>{" "}
            <span className="text-muted-foreground">al</span>{" "}
            <span className="font-medium">{rangoVenta.cierreTxt}</span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Define la ventana de venta para completar el resumen.
          </p>
        )}
      </div>

      {notifsActivas.length > 0 && (
        <div className="flex items-center flex-wrap gap-2">
          <span className="overline text-muted-foreground">
            Notificaciones activas
          </span>
          {notifsActivas.map((n) => (
            <span
              key={n}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 text-accent border border-accent/30 px-2.5 py-0.5 text-[11px] font-medium"
            >
              <Bell className="w-3 h-3" strokeWidth={2} />
              {n}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
